import "./styles.css";
import { detectText, type Finding } from "@ruja/detection-core";
import { createPlaceholderSession, type PlaceholderSession } from "@ruja/placeholders";

const EXAMPLE = "Email emma@northwind.se about the contract. Use IBAN DE89 3704 0044 0532 0130 00 and api_key: nordic-demo-secret-4821";

const byId = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!(element instanceof HTMLElement)) throw new Error(`MISSING_DEMO_ELEMENT:${id}`);
  return element as T;
};

const input = byId<HTMLTextAreaElement>("demo-input");
const output = byId<HTMLTextAreaElement>("demo-output");
const list = byId<HTMLUListElement>("demo-findings");
const empty = byId<HTMLElement>("demo-empty");
const count = byId<HTMLElement>("demo-count");
const findingCount = byId<HTMLElement>("demo-finding-count");
const selectionBar = byId<HTMLElement>("demo-selection-bar");
const selectionCount = byId<HTMLElement>("demo-selection-count");
const selectAll = byId<HTMLButtonElement>("demo-select-all");
const clearSelection = byId<HTMLButtonElement>("demo-clear-selection");

let findings: Finding[] = [];
let selected = new Set<string>();
let session: PlaceholderSession = createPlaceholderSession();

const updateCount = (): void => {
  count.textContent = `${input.value.length.toLocaleString("en")} / 4,000`;
};

const typeFor = (finding: Finding): string => {
  const types: Record<string, string> = {
    email: "EMAIL",
    iban: "IBAN",
    payment_card: "PAYMENT_CARD",
    se_personnummer: "SE_PERSON_ID",
    se_samordningsnummer: "SE_PERSON_ID",
    dk_cpr: "DK_CPR",
    lt_personal_code: "LT_PERSON_ID",
    bearer_token: "SECRET",
    jwt: "SECRET",
    assigned_secret: "SECRET",
    private_key: "SECRET",
  };
  return types[finding.rawType] ?? finding.rawType;
};

const renderPreview = (): void => {
  if (!findings.length || selected.size === 0) {
    output.value = "";
    return;
  }
  const selections = findings
    .filter((finding) => selected.has(finding.id))
    .map((finding) => ({
      startUtf16: finding.startUtf16,
      endUtf16: finding.endUtf16,
      type: typeFor(finding),
      canonicalValue: input.value.slice(finding.startUtf16, finding.endUtf16),
    }));
  output.value = session.protect(input.value, selections).text;
};

const updateSelection = (): void => {
  selectionBar.hidden = findings.length === 0;
  selectionCount.textContent = `${selected.size} of ${findings.length} selected`;
  selectAll.disabled = findings.length === 0 || selected.size === findings.length;
  clearSelection.disabled = selected.size === 0;
};

const renderFindings = (): void => {
  list.replaceChildren();
  list.hidden = findings.length === 0;
  empty.hidden = findings.length > 0;
  findingCount.textContent = findings.length === 0 ? "No findings" : `${findings.length} ${findings.length === 1 ? "finding" : "findings"}`;

  for (const finding of findings) {
    const item = document.createElement("li");
    const label = document.createElement("label");
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = selected.has(finding.id);
    checkbox.addEventListener("change", () => {
      if (checkbox.checked) selected.add(finding.id);
      else selected.delete(finding.id);
      label.classList.toggle("is-selected", checkbox.checked);
      updateSelection();
      renderPreview();
    });
    const copy = document.createElement("span");
    const title = document.createElement("strong");
    const value = document.createElement("small");
    title.textContent = finding.displayType;
    value.textContent = input.value.slice(finding.startUtf16, finding.endUtf16);
    copy.append(title, value);
    const state = document.createElement("span");
    state.className = finding.status === "validated" ? "validated" : "candidate";
    state.textContent = finding.status === "validated" ? "Validated" : "Review";
    label.append(checkbox, copy, state);
    item.append(label);
    list.append(item);
  }
  updateSelection();
};

const review = (): void => {
  selected = new Set();
  session.clear();
  session = createPlaceholderSession();
  findings = input.value.trim()
    ? detectText(input.value, { countries: ["se", "dk", "lt"], maxInputLength: 4_000 }).findings
    : [];
  renderFindings();
  renderPreview();
};

input.addEventListener("input", () => {
  findings = [];
  selected.clear();
  output.value = "";
  list.hidden = true;
  empty.hidden = false;
  empty.textContent = "Review the updated text to refresh local findings.";
  findingCount.textContent = "Not reviewed";
  updateSelection();
  updateCount();
});
byId<HTMLButtonElement>("demo-review").addEventListener("click", review);
byId<HTMLButtonElement>("demo-reset").addEventListener("click", () => {
  input.value = EXAMPLE;
  updateCount();
  review();
});
selectAll.addEventListener("click", () => {
  selected = new Set(findings.map((finding) => finding.id));
  renderFindings();
  renderPreview();
});
clearSelection.addEventListener("click", () => {
  selected.clear();
  renderFindings();
  renderPreview();
});

updateCount();
updateSelection();
